"use client";

/**
 * Auth gate for the Freshservice side panel — the Freshservice analogue of
 * `ProcoreAuthGate`. Two sequential steps:
 *
 *   1. **Sign in to Magnus** — Magnus identity is established in a
 *      first-party popup (`/freshservice/auth/start`) that mints a raw bearer
 *      session and hands it back via same-origin postMessage. We stash it in
 *      the generic `host-session-store` and inject it on every request via
 *      `getEmbeddedSessionHeaders`; the popup response also sets the
 *      partitioned `SameSite=None` session cookie for RSC navigations.
 *   2. **Connect Freshservice** — an API-key connector (no OAuth popup):
 *      the user pastes their Freshservice API key in Settings → Connectors.
 *      We open settings in a top-level tab; the probe refetches on window
 *      focus, so returning to the panel flips the gate to ready.
 *
 * Popups require a user gesture, so each step sits behind a button click.
 */
import { c as _c } from "react/compiler-runtime";
import { TRPCClientError } from "@trpc/client";
import { FRESHSERVICE_SURFACE } from "@workspace/copilots";
import { usePathname } from "next/navigation";
import { type ReactNode, useCallback, useEffect } from "react";
import { EmbeddedGateScreen } from "@/components/embedded/gate-screen";
import { isFreshserviceAuthMessage } from "@/lib/freshservice-auth";
import { clearHostSessionToken, setHostSessionToken } from "@/lib/host-session-store";
import { settingsHref } from "@/lib/settings-href";
import { trpc } from "@/trpc/client";
type AuthStep = "signin" | "connect" | "ready";
export function FreshserviceAuthGate(t0) {
  const $ = _c(16);
  const {
    children
  } = t0;
  const pathname = usePathname();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      retry: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const connectorsQuery = trpc.connectors.list.useQuery(undefined, t1);
  const hasMagnusSession = connectorsQuery.isSuccess;
  const magnusChecked = !connectorsQuery.isPending;
  let t2;
  let t3;
  if ($[1] !== connectorsQuery.error) {
    t2 = () => {
      const error = connectorsQuery.error;
      if (error instanceof TRPCClientError && error.data?.code === "UNAUTHORIZED") {
        clearHostSessionToken(FRESHSERVICE_SURFACE);
      }
    };
    t3 = [connectorsQuery.error];
    $[1] = connectorsQuery.error;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== connectorsQuery) {
    t4 = () => {
      const onMessage = event => {
        if (event.origin !== window.location.origin) {
          return;
        }
        if (!isFreshserviceAuthMessage(event.data)) {
          return;
        }
        setHostSessionToken(FRESHSERVICE_SURFACE, event.data.sessionToken);
        connectorsQuery.refetch();
      };
      window.addEventListener("message", onMessage);
      return () => window.removeEventListener("message", onMessage);
    };
    t5 = [connectorsQuery];
    $[4] = connectorsQuery;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  const freshservice = connectorsQuery.data?.find(_temp);
  const freshserviceConnected = freshservice?.isConnected ?? false;
  const freshserviceComingSoon = freshservice?.isComingSoon ?? false;
  const handleSignIn = _temp2;
  const handleConnect = _temp3;
  if (pathname?.startsWith("/freshservice")) {
    let t6;
    if ($[7] !== children) {
      t6 = <>{children}</>;
      $[7] = children;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    return t6;
  }
  let step = "signin";
  if (hasMagnusSession) {
    step = freshserviceConnected ? "ready" : "connect";
  }
  if (!magnusChecked) {
    let t6;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <EmbeddedGateScreen message={"Loading Magnus\u2026"} spinner={true} />;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    return t6;
  }
  if (step === "ready") {
    let t6;
    if ($[10] !== children) {
      t6 = <>{children}</>;
      $[10] = children;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    return t6;
  }
  if (step === "connect" && connectorsQuery.isLoading) {
    let t6;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <EmbeddedGateScreen message={"Checking your Freshservice connection\u2026"} spinner={true} />;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  if (step === "connect" && freshserviceComingSoon) {
    let t6;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <EmbeddedGateScreen message="Magnus for Freshservice isn't enabled for your account yet. Contact your administrator to request access." title="Freshservice access not enabled" />;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    return t6;
  }
  if (step === "connect") {
    let t6;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <EmbeddedGateScreen action={{
        label: "Open connector settings",
        onClick: handleConnect
      }} message={"Connect your Freshservice API key in Settings \u2192 Connectors, then return to this panel."} title="Connect Freshservice" />;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    return t6;
  }
  let t6;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <EmbeddedGateScreen action={{
      label: "Sign in to Magnus",
      onClick: handleSignIn
    }} message="Sign in with your Magnus account to use the assistant inside Freshservice." title="Sign in to Magnus" />;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
function _temp3() {
  window.open(settingsHref("connectors"), "_blank", "noopener");
}
function _temp2() {
  window.open("/freshservice/auth/start", "magnus-freshservice-auth", "width=480,height=640,popup=yes");
}
function _temp(c) {
  return c.id === "freshservice";
}